import { useMemo } from 'react';
import { DashboardData, DateRange, ValidationResult } from '../dtc-commerce-mmm-types';
import { filterDateWindow } from '../utils/dtc-commerce-mmm-data-loader';
import { aggregateTwoStageDecomp } from '../utils/dtc-commerce-mmm-two-stage';

/**
 * Re-derives date-windowed fields whenever the date range changes. Components keep
 * reading `data.actualized.cptbT12M` etc.; the field name is legacy but now reflects
 * the user-selected window.
 */
export const useFilteredDashboardData = (
  data: DashboardData | null,
  dateRange: DateRange
): DashboardData | null =>
  useMemo(() => {
    if (!data) return null;

    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return data;

    const actCptbFiltered = filterDateWindow(data.actualized.cptb, startDate, endDate);
    const rawCptbFiltered = filterDateWindow(data.raw.cptb, startDate, endDate);
    const avpFiltered = filterDateWindow(data.actualVsPredicted, startDate, endDate);
    const twoStageDecomp = aggregateTwoStageDecomp(
      data.twoStageDecompRows,
      data.actualVsPredicted,
      startDate,
      endDate
    );

    const totalBaseline = avpFiltered.reduce((s, r) => s + r.baselinePrediction, 0);
    const totalChannelContrib = actCptbFiltered.reduce((s, r) => s + r.effectiveNormalized, 0);
    const totalActualKpi = avpFiltered.reduce((s, r) => s + r.actualKpi, 0);
    const combined = totalBaseline + totalChannelContrib;
    const pctOfActual = totalActualKpi > 0 ? (combined / totalActualKpi) * 100 : 0;
    const passed =
      totalActualKpi > 0 && Math.abs(combined - totalActualKpi) / totalActualKpi < 0.05;

    const validation: ValidationResult = {
      totalBaseline,
      totalChannelContrib,
      totalActualKpi,
      pctOfActual,
      passed
    };

    return {
      ...data,
      actualized: { ...data.actualized, cptbT12M: actCptbFiltered },
      raw: { ...data.raw, cptbT12M: rawCptbFiltered },
      actualVsPredictedT12M: avpFiltered,
      twoStageDecomp,
      t12mStartDate: startDate,
      t12mEndDate: endDate,
      validation
    };
  }, [data, dateRange]);
